import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { cloneDeep, flatten, get, sumBy, map } from 'lodash-es';
import moment from 'moment-timezone';
import axios from 'axios';
import ReactLoading from 'react-loading';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPrint,
  faPlus,
  faEnvelope,
  faStickyNote,
  faEllipsisV,
  faQuestionCircle,
  faTruck,
} from '@fortawesome/free-solid-svg-icons';
import { Button,
  Col,
  Container,
  Input,
  Row,
  Table,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'reactstrap';
import Tooltip from '@mui/material/Tooltip';
import ReactToPrint, { useReactToPrint } from 'react-to-print';
import { shippingApi } from '../../../api/shipping';
import { preformatFloat, totalItemsCount, locale, formatCurrencyForLocale } from '../../../utilities/invoiceUtils';
import { useAxios } from '../../../hooks';
import { formatChannelText, titleCase } from '../../../utilities/textsFormatters';
import Page from '../../Page';
import AddChargesModal from '../AddChargesModal';
import AddNoteModal from '../AddNoteModal';
import ContactModal from '../ContactModal';
import PrintableCreateInvoices from './PrintableCreateInvoices';
import { cfPath, awsServer } from '../../../utilities/UrlPaths';
import { selectPolicyID, selectPolicyTerms, selectHouseID } from '../../../selectors';
import { SHIPPING_METHODS_MAP } from '../constants';

const blockEl = 'invoices__create-new-invoices';

const CreateNewByLotInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [checkedInvoices, setCheckedInvoices] = useState({});
  const [printableInvoices, setPrintableInvoices] = useState([]);
  const [checkedAllInvoicesIndicator, setCheckAllInvoicesIndicator] = useState(true);
  const [currentInvoiceIndex, setCurrentInvoiceIndex] = useState(null);
  const [isAddChargesModalOpen, setIsAddChargesModalOpen] = useState(false);
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [invoicesNotes, setInvoicesNotes] = useState({});
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [noneSelected, setNoneSelected] = useState(true);
  const [allOrSelected, setAllOrSelected] = useState('All');
  const [isOpenInvoiceItemStatusMenu, setIsOpenInvoiceItemStatusMenu] = useState({});
  const [totalPendingCharges, setTotalPendingCharges] = useState('0.00');
  const [pendingTotalRandomNumber, setPendingTotalRandomNumber] = useState(Math.random());
  const [invoiceTotal, setInvoiceTotal] = useState('0.00');
  const [paymentProcessorType, setPaymentProcessorType] = useState('');
  const [pickupShipUpdateObject, setPickupShipUpdateObject] = useState({});
  const history = useHistory();
  const { invoiceGroupID } = useParams();
  const { houseRef, paymentProcessingEnabled, autoChargeEnabled, houseUserName } = useSelector(state => state.house);
  const user = useSelector(state => state.user);
  const printRef = useRef();
  const isInvShippingAvailable = useSelector(selectPolicyID);
  const policyTerms = useSelector(selectPolicyTerms);
  const houseID = useSelector(selectHouseID);
  const [reviewIdToTermIdMap, setReviewIdToTermIdMap] = useState({});

  const {
    data: groupDetailInfoData,
    exec: execGroupDetailData,
  } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef]
  );
  const { exec: execNotes, isError: isNotesError } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { exec: execMessage } = useAxios(
    () => `/app/house/${houseRef}/invoiceMessage`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { execAsync: execAsyncAdditionalCharges } = useAxios(
    () => `/app/house/${houseRef}/additionalCharges`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );
  const { data: paymentProcessorData } = useAxios(
    () => `/app/house/${houseRef}/paymentProcessorType`,
    [houseRef]
  );
  const { exec: execPickupShip, data: pickupShipData } = useAxios(
    () => `/app/house/${houseRef}/invoiceGroup/${invoiceGroupID}/groupDetailInfo`,
    [houseRef],
    { method: 'post' },
    { manual: true }
  );

  const formatMomentDateToUTC = (date) => moment(moment(date, 'MM/DD/YYYY h:mm A zz').utc().format('MM/DD/YYYY'));

  useEffect(() => {
    if (typeof groupDetailInfoData === 'undefined') {
      return;
    }

    setInvoices(get(groupDetailInfoData, 'data.data.invoiceBidders', []));
    setPrintableInvoices(get(groupDetailInfoData, 'data.data.invoiceBidders', []));
  }, [groupDetailInfoData]);

  useEffect(() => {
    if (isNotesError) {
      setInvoicesNotes((prev) => {
        const { [`_${currentInvoiceIndex}`]: _, ...restInvoicesNotes } = prev;

        return restInvoicesNotes;
      });
    }
  }, [isNotesError]);

  useEffect(() => {
    if (!invoices) return;
    if (invoices.length > 0) setCurrencySymbol(invoices[0].totalPrice.currencySymbol);
    // fetch shipping reviews to get the related termID
    const uniqueShippingReviewIDs = invoices.reduce((acc, invoice) => {
      if (invoice?.invoiceDetails) {
        invoice.invoiceDetails.forEach((detail) => {
          if (detail?.shippingReviewID) acc.add(detail?.shippingReviewID);
        });
      }
      return acc;
    }, new Set());
    Promise.all(Array.from(uniqueShippingReviewIDs).map(async (reviewID) => {
      const review = await shippingApi.fetchReviewById(reviewID, houseID);
      return { reviewID, review: review.data.review };
    })).then((results) => {
      const shippingReviewIdToTermIDMap = results.reduce((acc, result) => {
        acc[result.reviewID] = result.review.arguments.termsID;
        return acc;
      }, {});
      setReviewIdToTermIdMap(shippingReviewIdToTermIDMap);
    });
  }, [invoices]);

  useEffect(() => {
    if (typeof paymentProcessorData === 'undefined') {
      return;
    }
    setPaymentProcessorType(get(paymentProcessorData, 'data.data.processor-type', ''));
  }, [paymentProcessorData]);

  const sessionsInGroup = useMemo(
    () => get(groupDetailInfoData, 'data.data.catalogs', []),
    [groupDetailInfoData]
  );

  const allInvoicesDetails = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    return flatten(map(invoices, 'invoiceDetails'));
  }, [invoices]);

  const allInvoicesIds = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    return map(invoices, 'invoiceID');
  }, [invoices]);

  const sortedSessionsInGroup = useMemo(() => {
    if (typeof sessionsInGroup === 'undefined' || !sessionsInGroup.length) {
      return [];
    }

    return cloneDeep(sessionsInGroup)
      .sort((a, b) => a.identifier.normalize()
        .localeCompare(b.identifier.normalize()));
  }, [sessionsInGroup]);

  const firstSessionGroupDate = useMemo(() => {
    if (typeof sessionsInGroup === 'undefined' || !sessionsInGroup.length) {
      return;
    }

    const [{ date }] = sessionsInGroup.length === 1
      ? sessionsInGroup
      : cloneDeep(sessionsInGroup).sort((a, b) => formatMomentDateToUTC(a.date).diff(
        formatMomentDateToUTC(b.date, 'days')
      ));

    return formatMomentDateToUTC(date);
  }, [sessionsInGroup]);

  const getDiffDateNumber = useCallback(
    (catalogDate) => {
      if (
        typeof firstSessionGroupDate === 'undefined'
        || !firstSessionGroupDate.isValid()
      ) {
        return null;
      }

      return (
        formatMomentDateToUTC(catalogDate).diff(firstSessionGroupDate, 'days')
        + 1
      );
    },
    [firstSessionGroupDate]
  );

  useEffect(() => {
    let theTotal = parseFloat(0.00);
    let thePendingTotal = parseFloat(0.00);

    if (allInvoicesDetails.length) {
      allInvoicesDetails.forEach((item) => {
        if (!item.suppress) {
          theTotal += parseFloat(preformatFloat(item.totalAmount));
          if (!item.paid) {
            thePendingTotal += parseFloat(preformatFloat(item.totalAmount));
          }
        }
      });
    }

    setTotalPendingCharges(thePendingTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    setInvoiceTotal(theTotal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  }, [invoices, pendingTotalRandomNumber]);

  const getOutstandingBalancePerInvoice = useCallback(
    (invoiceId) => {
      const currentInvoice = invoices.find(
        (invoice) => invoice.invoiceID === invoiceId
      );

      if (!currentInvoice) {
        return '0.00';
      }

      const { invoiceDetails } = currentInvoice;

      if (
        typeof invoiceDetails === 'undefined'
        || (Array.isArray(invoiceDetails) && !invoiceDetails.length)
      ) {
        return '0.00';
      }

      const outstandingBal = sumBy(
        invoiceDetails,
        (invoice) => parseFloat(preformatFloat(invoice.suppress ? 0 : invoice.totalAmount || 0))
      );

      return outstandingBal.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    [invoices]
  );

  const onlyCheckedInvoices = useMemo(
    () => Object.keys(checkedInvoices).reduce((acc, current) => {
      if (checkedInvoices[current]) {
        acc.push(Number(current));
      }

      return acc;
    }, []),
    [checkedInvoices]
  );

  useEffect(() => {
    if (!onlyCheckedInvoices.length) {
      setCheckAllInvoicesIndicator(true);
    }
    setNoneSelected(onlyCheckedInvoices.length === 0);

    if (onlyCheckedInvoices.length === invoices.length || onlyCheckedInvoices.length === 0) {
      setAllOrSelected('All');
    } else {
      setAllOrSelected('Selected');
    }

    const printableArray = invoices.filter(invoice => onlyCheckedInvoices.indexOf(invoice.invoiceID) > -1);
    setPrintableInvoices(onlyCheckedInvoices.length > 0 ? printableArray : invoices);
  }, [onlyCheckedInvoices]);

  const invoicesCharges = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length) {
      return [];
    }

    // eslint-disable-next-line no-nested-ternary
    const filteredInvoicesByIndex = currentInvoiceIndex !== null
      ? [invoices[currentInvoiceIndex]]
      : !onlyCheckedInvoices.length
        ? invoices
        : invoices.filter(({ invoiceID }) => onlyCheckedInvoices.find((id) => id === invoiceID));

    return filteredInvoicesByIndex.map(
      ({ invoiceID,
        firstName,
        lastName,
        invoiceDetails,
        paddleNumber,
        city,
        state,
        province,
        country,
        channel,
      }) => ({
        name: `${lastName}, ${firstName}`,
        id: invoiceID,
        outstandingBalance: getOutstandingBalancePerInvoice(invoiceID),
        paddleNumber,
        location: `${city}, ${state}${province} ${country}`,
        channel,
        country,
        charges: invoiceDetails.map(
          ({ lotID, lotNum, lotNumExt, lotURL, priceInfo, suppress, restricted, lotTitle }) => ({
            suppress,
            restricted,
            lotId: lotID,
            lotURL,
            lotDisplayName: `${lotNum}${lotNumExt}`,
            tax: priceInfo.tax || '0.00',
            shipping: priceInfo.shipping || '0.00',
            insurance: priceInfo.insurance || '0.00',
            other: priceInfo.other || '0.00',
            hammer: priceInfo.hammer || '0.00',
            premium: priceInfo.premium || '0.00',
            livefee: priceInfo.livefee || '0.00',
            lotTitle,
          })
        ),
      })
    );
  }, [invoices, currentInvoiceIndex, onlyCheckedInvoices]);

  const currentInvoiceNotes = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length || typeof invoices[currentInvoiceIndex] === 'undefined') {
      return '';
    }

    return invoicesNotes[`_${currentInvoiceIndex}`] || invoices[currentInvoiceIndex].notes || '';
  }, [invoices, currentInvoiceIndex, invoicesNotes]);

  const modalNameObject = useMemo(() => {
    if (typeof invoices === 'undefined' || !invoices.length || typeof invoices[currentInvoiceIndex] === 'undefined') {
      return '';
    }

    return {
      name: `${invoices[currentInvoiceIndex].lastName}, ${invoices[currentInvoiceIndex].firstName}`,
      paddleNumber: invoices[currentInvoiceIndex].paddleNumber,
      id: invoices[currentInvoiceIndex].invoiceID,
      location: `${invoices[currentInvoiceIndex].city}, ${invoices[currentInvoiceIndex].state}${invoices[currentInvoiceIndex].province} ${invoices[currentInvoiceIndex].country}`,
      channel: invoices[currentInvoiceIndex].channel,
    };
  }, [invoices, currentInvoiceIndex]);

  const getInvoiceDetailStatus = (invoiceSentDate, shippedDate, paidDate) => {
    let returnString = 'Not Sent';
    if (shippedDate) {
      returnString = 'Shipped';
    } else if (paidDate) {
      returnString = 'Paid';
    } else if (invoiceSentDate) {
      returnString = 'Sent';
    }
    return returnString;
  };

  const handleOnCheckedAllInvoices = (markAsChecked) => () => {
    const checkedAllInvoices = invoices.reduce((acc, current) => {
      acc[current.invoiceID] = markAsChecked;

      return acc;
    }, {});

    setCheckedInvoices(checkedAllInvoices);
    setCheckAllInvoicesIndicator(!markAsChecked);
    setCurrentInvoiceIndex(null);
  };

  const handleOnChangeInvoiceChecked = (invoiceId) => () => {
    setCheckedInvoices((prev) => ({
      ...prev,
      [invoiceId]: !prev[invoiceId],
    }));
  };

  const handleOnSetCurrentInvoiceIndex = (index) => () => {
    if (index === currentInvoiceIndex) {
      return;
    }

    setCurrentInvoiceIndex(index);
  };

  const handleOnAddNote = (notes) => {
    const currentInvoice = invoices[currentInvoiceIndex];

    if (
      !notes
      || typeof currentInvoice === 'undefined'
      || !currentInvoice.invoiceID
    ) {
      return;
    }

    execNotes({
      data: {
        invoiceNotes: [
          {
            notes,
            invoiceID: currentInvoice.invoiceID,
          },
        ],
      },
    });

    setTimeout(() => {
      const icon = document.getElementById(`noteIcon_${currentInvoice.invoiceID}`);
      icon.classList.add('has-note');
    }, 500);

    setInvoicesNotes((prev) => ({
      ...prev,
      [`_${currentInvoiceIndex}`]: notes
    }));
    setIsAddNoteModalOpen(false);
  };

  const handleOnSendMessage = (payload) => {
    const currentInvoice = invoices[currentInvoiceIndex];

    if (
      typeof houseRef === 'undefined'
      || typeof currentInvoice === 'undefined'
      || !currentInvoice.memberID
      || typeof payload !== 'object'
      || !Object.keys(payload).length
    ) {
      return;
    }

    execMessage({
      data: {
        ...payload,
        houseUserRef: user.userRef,
        bidderID: currentInvoice.memberID
      },
    });
    setIsContactModalOpen(false);
  };

  const handleOnPreviewSendAll = () => {
    if (!invoiceGroupID || !allInvoicesIds.length) {
      return;
    }

    // set flag for link on invoice
    window.sessionStorage.setItem('fromCreateInvoices', 'true');
    if (paymentProcessorType === 'p') {
      window.sessionStorage.setItem('autoChargeSource', 'create');
    } else {
      window.sessionStorage.removeItem('autoChargeSource');
    }
    const previewRedirectPage = autoChargeEnabled && paymentProcessorType === 'p' ? 'InvoiceAutoCharge' : 'InvoiceGroupPreview';
    history.push(
      `/invoices/${previewRedirectPage}/${invoiceGroupID}/invoice/${onlyCheckedInvoices.join(
        ','
      )}`,
      { replace: true }
    );
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const handleOnOpenInvoiceItemStatusMenu = (index) => () => {
    setIsOpenInvoiceItemStatusMenu((prev) => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const hideShowItem = async (e) => {
    const type = e.currentTarget.id.substring(0, 4);
    const invoiceDetailID = e.currentTarget.id.substring(5);
    const invoiceID = e.currentTarget.attributes.invoice.value;
    const suppress = (type === 'hide');

    await execNotes({
      data: {
        invoiceDetails: [
          {
            invoiceID,
            invoiceDetailID,
            suppress,
          },
        ],
        invoiceDetailPrice: [
          {
            invoiceID,
            invoiceDetailID,
            suppress,
          },
        ],
      },
    });
    //  above code sends to DB. Below updates local object & screen
    if (!isNotesError) {
      const invoicesCopy = invoices;
      invoicesCopy.forEach((invoice) => {
        if (invoice.invoiceID === Number(invoiceID)) {
          invoice.invoiceDetails.forEach((detail) => {
            if (detail.invoiceDetailID === Number(invoiceDetailID)) {
              detail.suppress = suppress;
              detail.priceInfo.suppress = suppress;
            }
          });
        }
      });
      setInvoices(invoicesCopy);
      setPendingTotalRandomNumber(Math.random());
    }
  };

  const invoiceHasRestrictedItems = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    const { invoiceDetails } = currentInvoice;

    let returnFlag = false;
    invoiceDetails.forEach((item) => {
      if (get(item, 'restricted', false)) {
        returnFlag = true;
      }
    });

    return returnFlag;
  };

  const isUsPayrixUser = (id) => {
    const currentInvoice = invoices.find(
      (invoice) => invoice.invoiceID === id
    );
    return (paymentProcessorType === 'p' && currentInvoice.country === 'US');
  };

  const handleOnSaveInvoiceCharges = async (invoice) => {
    // first check for changes and create updatedCharges array
    const currentInvoice = invoices.find(
      (myInvoice) => myInvoice.invoiceID === invoice.id
    );
    const { invoiceDetails } = currentInvoice;
    const updatedCharges = [];
    const updatedFloorCharges = [];

    invoice.charges.forEach(lot => {
      // get original lot data
      const currentLot = invoiceDetails.find(
        (myDetail) => myDetail.lotID === lot.lotId
      );
      if (
        currentLot.priceInfo.tax !== lot.tax
        || currentLot.priceInfo.shipping !== lot.shipping
        || currentLot.priceInfo.insurance !== lot.insurance
        || currentLot.priceInfo.other !== lot.other
      ) {
        updatedCharges.push(lot);
      }
      // floor charges
      if (currentLot.priceInfo.hammer !== lot.hammer
        || currentLot.priceInfo.premium !== lot.premium
        || currentLot.priceInfo.livefee !== lot.livefee) {
        updatedFloorCharges.push(lot);
      }
    });

    try {
      if (updatedCharges.length > 0) {
        const res = await Promise.all(
          updatedCharges.map(
            ({ lotDisplayName, lotId, ...restCharges }) => execAsyncAdditionalCharges({
              data: {
                suppress: restCharges.suppress,
                restricted: restCharges.restricted,
                paid: restCharges.paid,
                ...(!isUsPayrixUser(invoice.id) && { tax: parseFloat(preformatFloat(restCharges.tax)) }),
                shipping: parseFloat(preformatFloat(restCharges.shipping)),
                insurance: parseFloat(preformatFloat(restCharges.insurance)),
                other: parseFloat(preformatFloat(restCharges.other)),
                invoiceID: invoice.id,
                lotID: lotId
              },
              headers: { Authorization: `${houseRef}*` }
            })
          )
        );
      }

      if (updatedFloorCharges.length > 0) {
        //  build the payload
        const lotObjects = [];
        updatedFloorCharges.map(lot => {
          const thisLot = {
            lotId: lot.lotId,
            hammer: parseFloat(preformatFloat(lot.hammer)),
            buyersPremium: parseFloat(preformatFloat(lot.premium)),
            liveFee: parseFloat(preformatFloat(lot.livefee)),
          };
          lotObjects.push(thisLot);
        });

        const data = {
          type: 'invoice',
          id: invoice.id,
          attributes: {
            invoiceDetails: lotObjects,
          }
        };

        const postBody = { data };
        const postHeaders = {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            channel: 'infinitebidding',
            houseusername: houseUserName,
            Authorization: `${houseRef}*`
          }
        };

        await axios.patch(`/app/house/${houseRef}/invoice/${invoice.id}`, JSON.stringify(postBody), postHeaders);
      }
    } catch (error) {
      console.log('Error', error);
    }
  };

  const showColumn = (columnName, id) => {
    let returnFlag = false;
    let currentValue = 0;
    invoices.forEach((currentInvoice) => {
      if (currentInvoice.invoiceID === id) {
        currentInvoice.invoiceDetails.forEach((item) => {
          currentValue = item.priceInfo[columnName] || '0.00';
          if (currentValue !== '0.00') {
            returnFlag = true;
          }
        });
      }
    });
    return returnFlag;
  };

  const showPaddle = (paddle) => (paddle !== 0 && paddle !== '');

  const hasPendingPayment = (id, byLot = false) => {
    const foundInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    let isPendingPayment = false;
    if (byLot) {
      if (foundInvoice.invoiceDetails.every(item => !item.onlinePaymentStat === 'u')) {
        isPendingPayment = true;
      }
    } else {
      const paymentStat = get(foundInvoice, 'onlinePaymentStat', '');
      isPendingPayment = paymentStat === 'p' || paymentStat === 'u' || paymentStat === 'h';
    }

    return isPendingPayment;
  };

  const isFullyUnpaid = (id) => {
    const currentInvoice = invoices.find((invoice) => invoice.invoiceID === id);
    let returnFlag = true;

    if (currentInvoice.allPaid) {
      returnFlag = false;
    } else if (hasPendingPayment(currentInvoice.invoiceID)) {
      returnFlag = false;
    } else {
      for (let i = 0; i < currentInvoice.invoiceDetails.length; i++) {
        if (currentInvoice.invoiceDetails[i].paid || Number(currentInvoice.invoiceDetails[i].totalAmountPaid) > 0) {
          returnFlag = false;
          break;
        }
      }
    }

    return returnFlag;
  };

  const processPickupShip = async (e) => {
    // show spinner
    const list = document.getElementById(`bubbles_${e.currentTarget.id}`).classList;
    list.remove('d-none');
    const linkLabel = document.getElementById(`shippingLink_${e.currentTarget.id}`);
    const newText = e.currentTarget.attributes.changestate.value === 'shipping' ? 'pickup' : 'shipping';
    const changeElement = e.currentTarget.attributes.changestate;
    const updateObject = {
      linkLabel,
      changeElement,
      newText,
      id: e.currentTarget.id,
    };
    setPickupShipUpdateObject(updateObject);

    const postData = {
      deliveryMode: e.currentTarget.attributes.changestate.value,
      invoiceID: e.currentTarget.id,
    };

    try {
      await execPickupShip({
        data: {
          invoiceItemDeliveryModes: [postData]
        }
      });
    } catch (err) {
      console.log('processPickupShip post failure', err);
    }
  };

  useEffect(() => {
    if (get(pickupShipData, 'data.data.success', false)) {
      // make sure invoiceID from pickupShipData matches pickupShipUpdateObject before doing this !!!
      pickupShipUpdateObject.linkLabel.innerText = titleCase(pickupShipUpdateObject.newText);
      pickupShipUpdateObject.changeElement.value = pickupShipUpdateObject.newText;
      execGroupDetailData();
    }
  }, [pickupShipData]);

  useEffect(() => {
    const collection = document.getElementsByClassName('bubble-spinner');
    if (collection.length > 0) {
      for (let i = 0; i < collection.length; i++) {
        collection[i].classList.add('d-none');
      }
    }
  }, [invoices]);

  const getShippingTermName = (termID) => policyTerms.find(term => term.id === termID)?.name;

  const getShippingOptions = (termID) => {
    const term = policyTerms.find(term => term.id === termID);
    const shippingOptions = term?.shippingOptions.reduce((acc, option) => {
      acc.push(SHIPPING_METHODS_MAP[option.method]);
      return acc;
    }, []);
    return shippingOptions?.join(', ');
  };

  const isInvShipIncluded = (termID) => {
    const term = policyTerms.find(term => term.id === termID);
    return term ? term.shippingOptions.find(option => option.method === 'invaluable_shipping') : false;
  };

  return (
    <>
      <Page className={blockEl} header={{ title: 'Create New Invoices' }}>
        <Container>
          <Row className={`${blockEl}__header`}>
            <Col className={`${blockEl}__sessions-in-group`} xs="4">
              <Table borderless>
                <thead>
                  <tr>
                    <th>Sessions in Group</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSessionsInGroup.length
                    ? sortedSessionsInGroup.map((sessionGroup) => (
                      <tr key={sessionGroup.catalogID}>
                        <td>
                          {sessionGroup.identifier}: {sessionGroup.title} -
                          Day {getDiffDateNumber(sessionGroup.date)}
                        </td>
                      </tr>
                    ))
                    : null}
                </tbody>
              </Table>
            </Col>
            <Col className={`${blockEl}__totals`}>
              <Table borderless>
                <thead>
                  <tr>
                    <th>Total Invoices</th>
                    <th>Total Items</th>
                    <th>Invoice Total</th>
                    <th>Total Pending Charges</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>{invoices.length}</td>
                    <td>{totalItemsCount(allInvoicesDetails)}</td>
                    <td>{currencySymbol}{invoiceTotal}</td>
                    <td>{currencySymbol}{totalPendingCharges}</td>
                  </tr>
                </tbody>
              </Table>
            </Col>
          </Row>
          {!!isInvShippingAvailable && (
            <Row>
              <Col xs="8">
                <div className="my-2">
                  <div className="font-weight-bold">
                    Shipping
                  </div>
                  <div className="mt-1">
                    <img src={`${awsServer}/static/invShippingIcon.png`} alt="shipping truck" className="shippingTruckIcon height-20" />
                    <span> = Invaluable Shipping is available, do not add additional shipping charges for this lot</span>
                  </div>
                </div>
              </Col>
            </Row>
          )}
          <Row className={`${blockEl}__actions`}>
            <Col xs="4">
              {checkedAllInvoicesIndicator ? (
                <Button color="link" onClick={handleOnCheckedAllInvoices(true)}>
                  Select All Invoices
                </Button>
              ) : (
                <Button
                  color="link"
                  onClick={handleOnCheckedAllInvoices(false)}
                >
                  Deselect all
                </Button>
              )}
              {!noneSelected && (
              <Button
                color="link"
                onClick={() => {
                  setIsAddChargesModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
                Edit/Add Charges to {allOrSelected}
              </Button>

              )}
            </Col>
            <Col>
              <ReactToPrint
                documentTitle="InvaluableAuctions - invoice"
                trigger={() => (
                  <Button
                    color="link"
                    aria-label="print"
                    onClick={() => handlePrint()}
                  >
                    <FontAwesomeIcon icon={faPrint} /> Print {allOrSelected}
                  </Button>
                )}
                content={() => printRef.current}
              />

              <Button
                color="primary"
                onClick={handleOnPreviewSendAll}
                disabled={noneSelected}
                className={noneSelected ? 'disabled-cursor' : ''}
              >
                Preview &amp; Send {allOrSelected}
              </Button>

            </Col>
          </Row>
          {invoices.length
            ? invoices.map((invoice, index) => (
              <Row key={invoice.invoiceID} className={`${blockEl}__invoice`}>
                <Col xs={12} style={{ paddingBottom: 0 }}>
                  <Row>
                    <Col xs={8}>
                      <Row>
                        <Col xs="auto" className={`${blockEl}__invoice-checkbox`}>
                          <Input
                            type="checkbox"
                            key={Math.random()}
                            onClick={handleOnChangeInvoiceChecked(invoice.invoiceID)}
                            defaultChecked={checkedInvoices[invoice.invoiceID]}
                          />
                        </Col>
                        <Col className={`${blockEl}__invoice-details  mb-0`}>
                          <div className={`${blockEl}__invoice-details__name`}>
                            <h3>
                              <a href={`${cfPath()}/al/bidders/viewBidder.cfm?approvalID=${invoice.approvalID}`} target="_blank" rel="noopener noreferrer">
                                {invoice.lastName}, {invoice.firstName}
                              </a>
                              {showPaddle(invoice.paddleNumber) && (
                                <span className="small ml-2">
                                  (Paddle# {invoice.paddleNumber})
                                </span>
                              )}
                            </h3>
                          </div>
                          <div className="mb-2">
                            {invoice.email}
                          </div>
                          <div>
                            Invoice #{invoice.invoiceID} | {invoice.city},{' '}
                            {invoice.state || invoice.province},{' '} {invoice.country}
                            {' '}| {formatChannelText(invoice.channel)}
                          </div>
                          <Row>
                            <Col
                              xs={12}
                              className={`${blockEl}__invoice-details__actions`}
                              onClick={handleOnSetCurrentInvoiceIndex(index)}
                            >
                              <div className="float-left">
                                <Button
                                  color="link"
                                  onClick={() => {
                                    setIsAddChargesModalOpen(true);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPlus} />
                                  Edit/Add charges
                                </Button>

                              </div>
                              <div className="float-left ml-5">
                                <Button
                                  color="link"
                                  onClick={() => {
                                    setIsContactModalOpen(true);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faEnvelope} />
                                  Contact
                                </Button>
                              </div>
                              <div className="float-left ml-5">
                                <Button
                                  color="link"
                                  onClick={() => {
                                    setIsAddNoteModalOpen(true);
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faStickyNote}
                                    className={invoice.notes !== '' ? 'has-note' : ''}
                                    id={`noteIcon_${invoice.invoiceID}`}
                                  />
                                  Add internal note
                                </Button>
                              </div>
                              {isFullyUnpaid(invoice.invoiceID) && (
                                <>
                                  <div className="float-left ml-5">
                                    <Button
                                      color="link"
                                      id={invoice.invoiceID}
                                      changestate={invoice.pickup === undefined || !invoice.pickup ? 'pickup' : 'shipping'}
                                      onClick={(e) => { processPickupShip(e); }}
                                    >
                                      <FontAwesomeIcon icon={faTruck} />
                                      Mark as <span id={`shippingLink_${invoice.invoiceID}`}>{titleCase(invoice.pickup === undefined || !invoice.pickup ? 'pickup' : 'shipping')}</span>
                                    </Button>
                                  </div>
                                  <div id={`bubbles_${invoice.invoiceID}`} className="float-left bubble-spinner d-none">
                                    <ReactLoading
                                      className="mt-1"
                                      type="bubbles"
                                      color="black"
                                      height="45px"
                                      width="45px"
                                    />
                                  </div>
                                </>
                              )}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={{ size: 3, offset: 1 }} className={`${blockEl}__invoice-balance`}>
                      <div className="mr-2 float-right">
                        <h4 className="text-left">Outstanding Balance</h4>
                        <div className="text-right">
                          {currencySymbol} {getOutstandingBalancePerInvoice(invoice.invoiceID)}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {paymentProcessingEnabled && invoiceHasRestrictedItems(invoice.invoiceID) && (
                    <div className="alert alert-warning m2-3" role="alert">
                      <span className="alert-content">
                        <FontAwesomeIcon icon="credit-card" className="" />
                        <span className="pl-2">
                          One or more item(s) in this invoice are restricted and cannot be paid for online. Additional charges will need to be added on an item level.
                        </span>
                      </span>
                    </div>
                  )}

                </Col>
                <Col xs={12}>
                  <Row>
                    <Table
                      bordered
                      className={`${blockEl}__invoice__amounts`}
                    >
                      <thead>
                        <tr>
                          <th>Session</th>
                          <th>Lot</th>
                          <th>Hammer</th>
                          <th>BP</th>
                          {showColumn('livefee', invoice.invoiceID) && (
                            <th>Live Fee</th>
                          )}
                          {showColumn('tax', invoice.invoiceID) && (
                            <th>Tax</th>
                          )}
                          {showColumn('shipping', invoice.invoiceID) && (
                            <th>S/H</th>
                          )}
                          {showColumn('insurance', invoice.invoiceID) && (
                            <th>Insurance</th>
                          )}
                          {showColumn('other', invoice.invoiceID) && (
                            <th>
                              Other {' '}
                              <Tooltip title="This column is for adding or discounting any additional fees." placement="right">
                                <FontAwesomeIcon
                                  icon={faQuestionCircle}
                                />
                              </Tooltip>
                            </th>
                          )}
                          <th>Total</th>
                          <th>Invoice Status</th>
                          {!!isInvShippingAvailable && (
                            <th>Shipping Policy</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {invoice.invoiceDetails
                          && invoice.invoiceDetails.length
                          ? invoice.invoiceDetails.map((invoiceDetail) => (
                            <tr
                              key={invoiceDetail.invoiceDetailID}
                              className={`${blockEl}__invoice__item ${(invoiceDetail.suppress || invoiceDetail.priceInfo.suppress) ? `${blockEl}__invoice__hidden-item` : ''}`}
                            >
                              <td>{invoiceDetail.saleIdentifier}</td>
                              <td id={`lotnum_${invoiceDetail.lotRef}`}>
                                <Tooltip title={invoiceDetail.lotTitle} placement="right">
                                  <Button color="link">
                                    <a
                                      href={invoiceDetail.lotURL}
                                      className={(invoiceDetail.suppress || invoiceDetail.priceInfo.suppress) ? `${blockEl}__invoice__hidden-item` : ''}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {invoiceDetail.lotNum}
                                      {invoiceDetail.lotNumExt}
                                    </a>
                                  </Button>
                                  {paymentProcessingEnabled && invoiceDetail.restricted && ('*')}
                                </Tooltip>
                              </td>
                              <td>
                                <Row className="currency-table-cell">
                                  <Col>{currencySymbol}</Col>
                                  <Col>
                                    {formatCurrencyForLocale(invoiceDetail.priceInfo.hammer)}
                                  </Col>
                                </Row>
                              </td>
                              <td>
                                <Row className="currency-table-cell">
                                  <Col>{currencySymbol}</Col>
                                  <Col>
                                    {formatCurrencyForLocale(invoiceDetail.priceInfo.premium)}
                                  </Col>
                                </Row>
                              </td>
                              {showColumn('livefee', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>
                                      {formatCurrencyForLocale(invoiceDetail.priceInfo.livefee)}
                                    </Col>
                                  </Row>
                                </td>
                              )}
                              {showColumn('tax', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>{formatCurrencyForLocale(invoiceDetail.priceInfo.tax)}</Col>
                                  </Row>
                                </td>
                              )}
                              {showColumn('shipping', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>{formatCurrencyForLocale(invoiceDetail.priceInfo.shipping)}</Col>
                                  </Row>
                                </td>
                              )}
                              {showColumn('insurance', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>{formatCurrencyForLocale(invoiceDetail.priceInfo.insurance)}</Col>
                                  </Row>
                                </td>
                              )}
                              {showColumn('other', invoice.invoiceID) && (
                                <td>
                                  <Row className="currency-table-cell">
                                    <Col>{currencySymbol}</Col>
                                    <Col>{formatCurrencyForLocale(invoiceDetail.priceInfo.other)}</Col>
                                  </Row>
                                </td>
                              )}
                              <td>
                                <Row className="currency-table-cell">
                                  <Col>{currencySymbol}</Col>
                                  <Col>
                                    {formatCurrencyForLocale(invoiceDetail.totalAmount)}
                                  </Col>
                                </Row>
                              </td>
                              <td>
                                <Row className="pl-3 justify-content-between flex-nowrap">
                                  <span>
                                    {getInvoiceDetailStatus(
                                      invoice.invoiceSentDate,
                                      invoiceDetail.shippedDate,
                                      invoiceDetail.paidDate
                                    )}
                                  </span>
                                  <div>
                                    <Dropdown
                                      isOpen={isOpenInvoiceItemStatusMenu[invoiceDetail.invoiceDetailID] || false}
                                      direction="left"
                                      toggle={handleOnOpenInvoiceItemStatusMenu(invoiceDetail.invoiceDetailID)}
                                      className="create-dropdown"
                                    >
                                      <DropdownToggle className="ellipsis-shadow px-3">
                                        <FontAwesomeIcon icon={faEllipsisV} />
                                      </DropdownToggle>
                                      <DropdownMenu className={`${blockEl}__invoice__dropdown-link`}>
                                        {(invoiceDetail.suppress || invoiceDetail.priceInfo.suppress) ? (
                                          <DropdownItem
                                            id={`show_${invoiceDetail.invoiceDetailID}`}
                                            invoice={invoice.invoiceID}
                                            onClick={(e) => { hideShowItem(e); }}
                                          >
                                            Show on invoice
                                          </DropdownItem>
                                        ) : (
                                          <DropdownItem
                                            id={`hide_${invoiceDetail.invoiceDetailID}`}
                                            invoice={invoice.invoiceID}
                                            onClick={(e) => { hideShowItem(e); }}
                                          >
                                            Hide from invoice
                                          </DropdownItem>
                                        )}
                                      </DropdownMenu>
                                    </Dropdown>
                                  </div>
                                </Row>
                              </td>
                              {!!isInvShippingAvailable && (
                              <td>
                                {isInvShipIncluded(reviewIdToTermIdMap[invoiceDetail.shippingReviewID]) && <img src={`${awsServer}/static/invShippingIcon.png`} alt="shipping truck" className="shippingTruckIcon" />}
                                {getShippingTermName(reviewIdToTermIdMap[invoiceDetail.shippingReviewID])}
                                <Tooltip
                                  title={(
                                    <>
                                      <strong>Shipping Options</strong>
                                      <br />{getShippingOptions(reviewIdToTermIdMap[invoiceDetail.shippingReviewID])}
                                    </>
                                        )}
                                  placement="right"
                                >
                                  <FontAwesomeIcon
                                    icon={faQuestionCircle}
                                    id={`shipStsatus_${invoiceDetail.lotRef}`}
                                    className="cursor-pointer ml-2"
                                  />
                                </Tooltip>
                              </td>

                              )}
                            </tr>
                          ))
                          : null}
                      </tbody>
                    </Table>
                  </Row>
                </Col>
                {paymentProcessingEnabled && invoiceHasRestrictedItems(invoice.invoiceID) && (
                  <div className="ml-2 mt-3">
                    *this item is restricted and cannot be paid for online
                  </div>
                )}

              </Row>
            ))
            : null}

          {/* printable version */}
          {invoices.length && (
            <div className="d-none">
              <PrintableCreateInvoices
                ref={printRef}
                invoices={printableInvoices}
                getOutstandingBalancePerInvoice={getOutstandingBalancePerInvoice}
                getInvoiceDetailStatus={getInvoiceDetailStatus}
              />
            </div>
          )}
        </Container>
      </Page>
      <AddChargesModal
        isOpen={isAddChargesModalOpen}
        invoices={invoicesCharges}
        byInvoiceCharges={invoicesCharges}
        onClose={() => {
          setIsAddChargesModalOpen(false);
          execGroupDetailData();
        }}
        variant="lot"
        currencySymbol={currencySymbol}
        onSave={handleOnSaveInvoiceCharges}
        paymentProcessorType={paymentProcessorType}
        setIsAddChargesModalOpen={setIsAddChargesModalOpen}
        hideAdditionalCode={true}
        chargesByLot={true}
      />
      <AddNoteModal
        isOpen={isAddNoteModalOpen}
        notes={currentInvoiceNotes}
        modalNameObject={modalNameObject}
        onClose={() => {
          setIsAddNoteModalOpen(false);
        }}
        onSave={handleOnAddNote}
      />
      <ContactModal
        isOpen={isContactModalOpen}
        modalNameObject={modalNameObject}
        onClose={() => {
          setIsContactModalOpen(false);
        }}
        onSave={handleOnSendMessage}
      />
    </>
  );
};

export default CreateNewByLotInvoices;
